import { useEffect } from 'react'
import { Moon, Sun, Sunrise } from 'lucide-react'
import { Link, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { PageMetadata } from '@/components/common/PageMetadata'
import { useAuthStore } from '@/stores/useAuthStore'
import { LoginForm } from '@/features/auth/components/LoginForm'
import { LoginShowcase } from '@/features/auth/components/LoginShowcase'
import { LOGIN_COPY } from '@/features/auth/constants/loginCopy'
import { brandAssets } from '@/config/brandAssets'
import { AuthLayout, authLinkClass } from '@/features/auth/components/AuthLayout'
import { getPublicTenant } from '@/api/services/auth.service'
import { AppLoader } from '@/components/common/AppLoader'
import {
  apexUrl,
  currentTenantHostLabel,
  getTenantSlugFromHost,
  tenantHostLabel,
} from '@/lib/tenantHost'

function getGreeting(date: Date): { label: string; Icon: typeof Sun } {
  const hour = date.getHours()
  if (hour < 5 || hour >= 18) return { label: 'Good evening', Icon: Moon }
  if (hour < 12) return { label: 'Good morning', Icon: Sunrise }
  return { label: 'Good afternoon', Icon: Sun }
}

/** A tenant subdomain that doesn't match an active organization. */
function WorkspaceNotFound({ slug }: { slug: string }): React.ReactElement {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-white px-6 text-center">
      <PageMetadata />
      <img src={brandAssets.logo} alt="KAAMCO" className="mb-4 h-auto w-40 object-contain" />
      <h1 className="text-xl font-bold text-gray-900">Workspace not found</h1>
      <p className="max-w-md text-sm text-gray-600">
        There is no active KAAMCO workspace at{' '}
        <span className="font-medium text-gray-900">{tenantHostLabel(slug)}</span>. Check the
        address, or sign in from the main site to find yours.
      </p>
      <a
        href={apexUrl('/login')}
        className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
      >
        Go to KAAMCO sign in
      </a>
    </div>
  )
}

/**
 * Login page. Redirects to dashboard if already authenticated.
 * Rendered in light theme for consistent visibility and standard login UX.
 * On a tenant subdomain it shows that organization and signs in to it only.
 */
export function LoginPage(): React.ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const tenantSlug = getTenantSlugFromHost()
  const tenantQuery = useQuery({
    queryKey: ['public-tenant', tenantSlug],
    queryFn: () => getPublicTenant(tenantSlug as string),
    enabled: tenantSlug !== null && !isAuthenticated,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) =>
      !(isAxiosError(error) && error.response?.status === 404) && failureCount < 2,
  })
  const tenant = tenantQuery.data

  // Strip any credentials that may have leaked into the URL (e.g. from a
  // native GET form submit before React attached its handlers, or a stale
  // bookmark). These should never sit in the address bar or browser history.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const sensitive = ['password', 'email', 'organizationSlug']
    let dirty = false
    for (const key of sensitive) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key)
        dirty = true
      }
    }
    if (dirty) {
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash)
    }
  }, [])

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  if (tenantSlug && tenantQuery.isPending) {
    return <AppLoader />
  }

  if (tenantSlug && tenantQuery.isError) {
    if (isAxiosError(tenantQuery.error) && tenantQuery.error.response?.status === 404) {
      return <WorkspaceNotFound slug={tenantSlug} />
    }
    // Lookup failed for another reason: still let them sign in to this tenant.
  }

  const greeting = getGreeting(new Date())
  const linkClass = authLinkClass

  return (
    <AuthLayout
      aside={<LoginShowcase />}
      legalLinks={tenant?.branding}
      headerAction={
        tenantSlug ? null : (
          <>
            New here?{' '}
            <Link to="/signup" className={linkClass}>
              Create an organization
            </Link>
          </>
        )
      }
    >
      <PageMetadata />
      {tenant?.branding.logo && (
        <img
          src={tenant.branding.logo}
          alt={tenant.name}
          className="mb-6 h-12 w-auto max-w-[200px] object-contain"
        />
      )}
      <h1
        id="login-form-heading"
        className="font-display text-[2rem] font-semibold leading-[1.1] tracking-[-0.035em] text-balance sm:text-[2.25rem]"
      >
        {tenantSlug ? `Sign in to ${tenant?.name ?? tenantSlug}` : LOGIN_COPY.welcomeBack}
      </h1>
      <p className="mt-2 flex items-center gap-2 text-[0.9375rem] text-[var(--public-muted)]">
        {tenantSlug ? (
          currentTenantHostLabel(tenantSlug)
        ) : (
          <>
            <greeting.Icon className="h-4 w-4 shrink-0 text-accent" aria-hidden />
            {greeting.label}. {LOGIN_COPY.signInToContinue}.
          </>
        )}
      </p>
      <div className="mt-8">
        <LoginForm organizationSlug={tenantSlug ?? undefined} />
      </div>
      {tenantSlug ? (
        <p className="mt-8 text-sm text-[var(--public-muted)]">
          Not your workspace?{' '}
          <a href={apexUrl('/login')} className={linkClass}>
            Sign in from the main site
          </a>
        </p>
      ) : (
        <p className="mt-8 text-sm text-[var(--public-muted)] sm:hidden lg:block xl:hidden">
          New here?{' '}
          <Link to="/signup" className={linkClass}>
            Create an organization
          </Link>
        </p>
      )}
    </AuthLayout>
  )
}
